import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, Search, X, Star, Flame, Heart, Users } from 'lucide-react';
import { ChatBubble } from './ui/chat-bubble';
import { supabase } from '../lib/supabase';
import { Candidate, Evaluation, EvaluationComment } from '../lib/supabase';
import { UNIVERSITIES, getUniversity, type UniversityId } from '../lib/constants';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { collectRecipientsForTopic, recipientsForNewTopic } from '../lib/notificationRecipients';

interface CandidateWithEvaluationInfo extends Candidate {
  evaluations: Evaluation[];
}

// Send notification (fire-and-forget, log failures)
async function sendDiscussionNotification(eventType: string, candidateId: string, actorName: string, recipientEmail?: string, commentContent?: string) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return;
    fetch('/api/send-notification', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ event_type: eventType, candidate_id: candidateId, actor_name: actorName, recipient_email: recipientEmail, comment_content: commentContent }),
      keepalive: true,
    })
      .then(async (r) => {
        if (!r.ok) {
          const text = await r.text().catch(() => '');
          console.warn(`[notify] ${eventType} failed: HTTP ${r.status} ${text}`);
        }
      })
      .catch((err) => console.warn(`[notify] ${eventType} network error:`, err));
  } catch (err) {
    console.warn(`[notify] ${eventType} setup error:`, err);
  }
}

export default function PublicDiscussion() {
  const [candidates, setCandidates] = useState<CandidateWithEvaluationInfo[]>([]);
  const [selectedCandidate, setSelectedCandidate] = useState<CandidateWithEvaluationInfo | null>(null);
  const [selectedEvaluation, setSelectedEvaluation] = useState<Evaluation | null>(null);
  const [comments, setComments] = useState<EvaluationComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [newTopic, setNewTopic] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUserType, setCurrentUserType] = useState<'evaluator' | 'candidate' | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const currentUserIdRef = useRef<string | null>(null);
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [collapsedTopics, setCollapsedTopics] = useState<Record<string, boolean>>({});
  const [evaluatorById, setEvaluatorById] = useState<Record<string, {name: string; avatar_url?: string; university?: string; is_reviewer?: boolean}>>({});
  const [evalToEvaluator, setEvalToEvaluator] = useState<Record<string, string>>({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [commentCounts, setCommentCounts] = useState<Record<string, number>>({});
  const [recentCounts, setRecentCounts] = useState<Record<string, number>>({});
  const [recentLikeCounts, setRecentLikeCounts] = useState<Record<string, number>>({});
  const [lastCommentAt, setLastCommentAt] = useState<Record<string, string>>({});
  const [sortBy, setSortBy] = useState<'rating' | 'ratingCount' | 'comments' | 'newest' | 'university'>('comments');
  const [uniFilter, setUniFilter] = useState<Record<string, boolean>>({ gunma: true, chiba: true, tsukuba: true });
  const [commentContentByCandidate, setCommentContentByCandidate] = useState<Record<string, string>>({});
  const [myLikedList, setMyLikedList] = useState<{candidate_id: string; candidate_name: string; research_topic: string}[]>([]);
  const [myParticipateList, setMyParticipateList] = useState<{candidate_id: string; candidate_name: string; research_topic: string}[]>([]);
  const [myThreads, setMyThreads] = useState<{candidate_id: string; candidate_name: string; research_topic: string; topic: string; comment_count: number; last_commented_at: string}[]>([]);
  const [showMyActivity, setShowMyActivity] = useState(true);
  const [currentUserName, setCurrentUserName] = useState('参加者');
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    loadData();
    // Mark notifications as read when opening discussion page
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (cancelledRef.current) return;
        if (user?.email) {
          await supabase
            .from('notification_queue')
            .update({ read_at: new Date().toISOString() })
            .eq('recipient_email', user.email)
            .is('read_at', null);
        }
      } catch {}
    })();
    return () => { cancelledRef.current = true; };
  }, []);

  useEffect(() => {
    console.log('selectedCandidate changed:', selectedCandidate?.id, selectedCandidate?.name);
    if (selectedCandidate) {
      loadComments();
    } else {
      setComments([]);
    }
  }, [selectedCandidate]);

  // Auto-refresh comments every 30 seconds
  useEffect(() => {
    if (!selectedCandidate) return;
    const interval = setInterval(() => {
      if (!commentLoading) loadComments();
    }, 30000);
    return () => clearInterval(interval);
  }, [selectedCandidate, commentLoading]);

  const loadData = async () => {
    const isCancelled = () => cancelledRef.current;
    try {
      // Get current user type
      const { data: { user } } = await supabase.auth.getUser();
      if (isCancelled()) return;
      if (user) {
        currentUserIdRef.current = user.id;
        setCurrentUserId(user.id);
        setCurrentUserEmail(user.email ?? null);
        // Check admin
        try {
          const { data: adminRow } = await supabase
            .from('admin_users')
            .select('user_id')
            .eq('user_id', user.id)
            .maybeSingle();
          if (isCancelled()) return;
          setIsAdmin(!!adminRow);
        } catch {}
        const { data: candidateData } = await supabase
          .from('candidates')
          .select('*')
          .eq('email', user.email)
          .maybeSingle();
        if (isCancelled()) return;

        if (candidateData) {
          setCurrentUserType('candidate');
          setCurrentUserName(candidateData.name);
        } else {
          setCurrentUserType('evaluator');
        }
        // Get evaluator display name (everyone has an evaluator profile)
        const { data: evalProfile } = await supabase.from('evaluators').select('name').eq('email', user.email).maybeSingle();
        if (isCancelled()) return;
        if (evalProfile) {
          if (!candidateData) setCurrentUserName(evalProfile.name);
        }
      }

      // Load all candidates with their evaluations
      const { data: candidatesData, error: candidatesError } = await supabase
        .from('candidates')
        .select(`
          *,
          evaluations (*)
        `)
        .order('created_at', { ascending: false });

      if (isCancelled()) return;
      if (candidatesError) throw candidatesError;
      setCandidates(candidatesData || []);

      // Load comment counts per candidate（総数と直近5分）
      try {
        const { data: evals } = await supabase
          .from('evaluations')
          .select('id, candidate_id');
        if (isCancelled()) return;
        const evalToCandidate = new Map<string, string>();
        (evals || []).forEach((e: any) => evalToCandidate.set(e.id, e.candidate_id));

        const { data: comments } = await supabase
          .from('evaluation_comments')
          .select('evaluation_id, created_at, content');
        if (isCancelled()) return;
        const counts: Record<string, number> = {};
        const recent: Record<string, number> = {};
        const lastAt: Record<string, string> = {};
        const contentMap: Record<string, string> = {};
        const now = Date.now();
        (comments || []).forEach((r: any) => {
          const cid = evalToCandidate.get(r.evaluation_id);
          if (!cid) return;
          counts[cid] = (counts[cid] || 0) + 1;
          if (!lastAt[cid]) lastAt[cid] = r.created_at as string;
          const t = new Date(r.created_at as string).getTime();
          if (now - t <= 5 * 60 * 1000) {
            recent[cid] = (recent[cid] || 0) + 1;
          }
          // Aggregate comment content for full-text search
          if (r.content) {
            contentMap[cid] = (contentMap[cid] || '') + ' ' + r.content;
          }
        });
        setCommentCounts(counts);
        setRecentCounts(recent);
        setLastCommentAt(lastAt);
        setCommentContentByCandidate(contentMap);
      } catch {}

      // Load recent likes in last 5 minutes per candidate（liked=true && updated_at within 5m）
      try {
        const since = new Date(Date.now() - 5 * 60 * 1000).toISOString();
        const { data: recentLikes } = await supabase
          .from('evaluations')
          .select('candidate_id, updated_at')
          .eq('liked', true)
          .gte('updated_at', since);
        if (isCancelled()) return;
        const rl: Record<string, number> = {};
        (recentLikes || []).forEach((row: any) => {
          const cid = row.candidate_id as string;
          rl[cid] = (rl[cid] || 0) + 1;
        });
        setRecentLikeCounts(rl);
      } catch {}

      // Load my activity data (liked, participate, threads)
      if (user) {
        try {
          // Get evaluator id
          const { data: evalProfile } = await supabase.from('evaluators').select('id').eq('email', user.email).maybeSingle();
          if (isCancelled()) return;
          if (evalProfile?.id) {
            const evaluatorId = evalProfile.id;
            // Liked
            const { data: likes } = await supabase.from('evaluations').select('candidate_id').eq('evaluator_id', evaluatorId).eq('liked', true);
            if (isCancelled()) return;
            // Participate
            const { data: parts } = await supabase.from('evaluations').select('candidate_id').eq('evaluator_id', evaluatorId).eq('want_to_participate', true);
            if (isCancelled()) return;
            const allCandIds = Array.from(new Set([...(likes || []).map((r: any) => r.candidate_id), ...(parts || []).map((r: any) => r.candidate_id)]));
            if (allCandIds.length > 0) {
              const { data: cands } = await supabase.from('candidates').select('id, name, research_topic').in('id', allCandIds);
              if (isCancelled()) return;
              const cmap = new Map<string, any>((cands || []).map((c: any) => [c.id, c]));
              setMyLikedList((likes || []).map((r: any) => ({ candidate_id: r.candidate_id, candidate_name: cmap.get(r.candidate_id)?.name || '', research_topic: cmap.get(r.candidate_id)?.research_topic || '' })));
              setMyParticipateList((parts || []).map((r: any) => ({ candidate_id: r.candidate_id, candidate_name: cmap.get(r.candidate_id)?.name || '', research_topic: cmap.get(r.candidate_id)?.research_topic || '' })));
            }
          }
          // My threads
          const { data: myComments } = await supabase.from('evaluation_comments').select('evaluation_id, topic, created_at').eq('user_id', user.id).order('created_at', { ascending: false });
          if (isCancelled()) return;
          if (myComments && myComments.length > 0) {
            const evalIds = Array.from(new Set(myComments.map((c: any) => c.evaluation_id)));
            const { data: evals2 } = await supabase.from('evaluations').select('id, candidate_id').in('id', evalIds);
            if (isCancelled()) return;
            const e2c = new Map<string, string>((evals2 || []).map((e: any) => [e.id, e.candidate_id]));
            const candIds2 = Array.from(new Set((evals2 || []).map((e: any) => e.candidate_id)));
            const { data: cands2 } = await supabase.from('candidates').select('id, name, research_topic').in('id', candIds2);
            if (isCancelled()) return;
            const cm2 = new Map<string, any>((cands2 || []).map((c: any) => [c.id, c]));
            const groups = new Map<string, { candidate_id: string; topic: string; count: number; last: string }>();
            for (const c of myComments) {
              const cid = e2c.get(c.evaluation_id as string);
              if (!cid) continue;
              const topic = (c.topic as string) || 'general';
              const key = `${cid}__${topic}`;
              const prev = groups.get(key);
              if (!prev) { groups.set(key, { candidate_id: cid, topic, count: 1, last: c.created_at as string }); }
              else { prev.count += 1; if (new Date(c.created_at as string).getTime() > new Date(prev.last).getTime()) prev.last = c.created_at as string; }
            }
            const threads: any[] = [];
            for (const g of groups.values()) {
              const cand = cm2.get(g.candidate_id);
              if (!cand) continue;
              threads.push({ candidate_id: g.candidate_id, candidate_name: cand.name, research_topic: cand.research_topic, topic: g.topic, comment_count: g.count, last_commented_at: g.last });
            }
            threads.sort((a, b) => new Date(b.last_commented_at).getTime() - new Date(a.last_commented_at).getTime());
            setMyThreads(threads);
          }
        } catch {}
      }
    } catch (err: any) {
      if (!isCancelled()) setError(err.message);
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  };

  const loadComments = async () => {
    const isCancelled = () => cancelledRef.current;
    if (!selectedCandidate) {
      console.log('No candidate selected');
      return;
    }

    console.log('Loading comments for candidate:', selectedCandidate.id, selectedCandidate.name);
    setCommentsLoading(true);
    setError(null);

    try {
      // Load all comments for all evaluations of this candidate
      const { data: evaluations, error: evalError } = await supabase
        .from('evaluations')
        .select('id, evaluator_id')
        .eq('candidate_id', selectedCandidate.id);

      if (isCancelled()) return;
      if (evalError) {
        console.error('Error loading evaluations:', evalError);
        throw evalError;
      }

      console.log('Found evaluations:', evaluations?.length || 0);

      if (!evaluations || evaluations.length === 0) {
        console.log('No evaluations found for this candidate');
        setComments([]);
        setCommentsLoading(false);
        return;
      }

      const evaluationIds = evaluations.map(e => e.id);
      const evalMap: Record<string, string> = {};
      (evaluations || []).forEach((e: any) => { evalMap[e.id] = e.evaluator_id; });
      setEvalToEvaluator(evalMap);
      // Load evaluator display names
      const evaluatorIds = Array.from(new Set(evaluations.map((e: any) => e.evaluator_id).filter(Boolean)));
      if (evaluatorIds.length) {
        const { data: infos } = await supabase
          .from('evaluators')
          .select('id, name, avatar_url, university, is_reviewer')
          .in('id', evaluatorIds);
        if (isCancelled()) return;
        const map: Record<string, {name: string; avatar_url?: string; university?: string; is_reviewer?: boolean}> = {};
        (infos || []).forEach((ev: any) => { map[ev.id] = { name: ev.name, avatar_url: ev.avatar_url || undefined, university: ev.university || undefined, is_reviewer: !!ev.is_reviewer }; });
        setEvaluatorById(map);
      } else {
        setEvaluatorById({});
      }
      const { data, error } = await supabase
        .from('evaluation_comments')
        .select('*')
        .in('evaluation_id', evaluationIds)
        .order('created_at', { ascending: true });
      if (isCancelled()) return;
      if (error) throw error;
      const visible = (data || []) as EvaluationComment[];
      console.log('Loaded comments:', visible.length);
      setComments(visible);
    } catch (err: any) {
      console.error('loadComments error:', err);
      if (!isCancelled()) {
        setError(err.message);
        setComments([]);
      }
    } finally {
      if (!isCancelled()) setCommentsLoading(false);
    }
  };

  const handleSubmitComment = async () => {
    if ((!newComment.trim() && !newTopic.trim()) || !selectedCandidate || !currentUserType) return;

    setCommentLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('ユーザーが見つかりません');

      // Get or create an evaluation for commenting
      let evaluationId: string;
      
      if (selectedEvaluation) {
        evaluationId = selectedEvaluation.id;
      } else {
        // First, check if current user has an evaluator record
        const { data: evaluatorData } = await supabase
          .from('evaluators')
          .select('id')
          .eq('email', user.email)
          .maybeSingle();
          
        if (!evaluatorData) {
          throw new Error('参加者として登録されていません。参加者としてログインしてください。');
        }
        
        // Check if evaluation already exists for this user
        const { data: existingEval } = await supabase
          .from('evaluations')
          .select('id')
          .eq('candidate_id', selectedCandidate.id)
          .eq('evaluator_id', evaluatorData.id)
          .maybeSingle();
          
        if (existingEval) {
          evaluationId = existingEval.id;
        } else {
          // Create a minimal evaluation
          const { data: newEval, error: evalError } = await supabase
            .from('evaluations')
            .insert({
              candidate_id: selectedCandidate.id,
              evaluator_id: evaluatorData.id,
              rating: 5,
              liked: false,
              want_to_participate: false
            })
            .select()
            .single();
            
          if (evalError) throw evalError;
          evaluationId = newEval.id;
        }
        
        // Update selected evaluation
        setSelectedEvaluation({ id: evaluationId } as Evaluation);
      }

      // Determine user_type: if commenting on own research, use 'candidate', otherwise 'evaluator'
      const effectiveUserType = (currentUserType === 'candidate' && currentUserEmail === selectedCandidate?.email) ? 'candidate' : 'evaluator';

      // Get current user's display name
      let authorName = '参加者';
      const { data: myEval } = await supabase.from('evaluators').select('name').eq('email', user.email).maybeSingle();
      if (myEval) authorName = myEval.name;
      if (effectiveUserType === 'candidate') {
        const { data: myCand } = await supabase.from('candidates').select('name').eq('email', user.email).maybeSingle();
        if (myCand) authorName = myCand.name;
      }

      const topic = newTopic.trim() || 'general';
      const isExistingTopic = comments.some((c) => (c.topic ?? 'general') === topic);

      const { error } = await supabase
        .from('evaluation_comments')
        .insert({
          evaluation_id: evaluationId,
          user_id: user.id,
          user_type: effectiveUserType,
          author_name: authorName,
          author_email: user.email,
          content: newComment.trim() || `新しいトピック: ${newTopic.trim()}`,
          parent_comment_id: null,
          topic
        });

      if (error) throw error;

      const commentText = newComment.trim() || `新しいトピック: ${newTopic.trim()}`;
      if (selectedCandidate) {
        const recipients = isExistingTopic
          ? collectRecipientsForTopic(comments, topic, selectedCandidate.email, currentUserEmail)
          : recipientsForNewTopic(selectedCandidate.email, currentUserEmail);
        recipients.forEach((email) => {
          sendDiscussionNotification('comment', selectedCandidate.id, currentUserName, email, commentText);
        });
      }

      setNewComment('');
      setNewTopic('');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCommentLoading(false);
      try { await loadComments(); } catch {}
    }
  };

  const handleSubmitReply = async (parentId: string, topic: string) => {
    if (!replyContent.trim() || !currentUserType) return;

    setCommentLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('ユーザーが見つかりません');

      // Find the parent comment to get its evaluation_id
      const parentComment = comments.find(c => c.id === parentId);
      if (!parentComment) throw new Error('親コメントが見つかりません');

      const effectiveUserType = (currentUserType === 'candidate' && currentUserEmail === selectedCandidate?.email) ? 'candidate' : 'evaluator';

      let authorName = '参加者';
      const { data: myEval2 } = await supabase.from('evaluators').select('name').eq('email', user.email).maybeSingle();
      if (myEval2) authorName = myEval2.name;
      if (effectiveUserType === 'candidate') {
        const { data: myCand2 } = await supabase.from('candidates').select('name').eq('email', user.email).maybeSingle();
        if (myCand2) authorName = myCand2.name;
      }

      const { error } = await supabase
        .from('evaluation_comments')
        .insert({
          evaluation_id: parentComment.evaluation_id,
          user_id: user.id,
          user_type: effectiveUserType,
          author_name: authorName,
          author_email: user.email,
          content: replyContent.trim(),
          parent_comment_id: parentId,
          topic: topic
        });

      if (error) throw error;

      if (selectedCandidate) {
        const recipients = collectRecipientsForTopic(
          comments,
          topic,
          selectedCandidate.email,
          currentUserEmail
        );
        recipients.forEach((email) => {
          sendDiscussionNotification('comment', selectedCandidate.id, currentUserName, email, replyContent.trim());
        });
      }

      setReplyContent('');
      setReplyingTo(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCommentLoading(false);
      try { await loadComments(); } catch {}
    }
  };

  const filteredCandidates = candidates.filter(candidate => {
    // University filter
    if (!uniFilter[candidate.university]) return false;
    // Search filter
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const uni = getUniversity(candidate.university);
    return (
      candidate.name.toLowerCase().includes(term) ||
      candidate.affiliation.toLowerCase().includes(term) ||
      candidate.department?.toLowerCase().includes(term) ||
      candidate.research_topic.toLowerCase().includes(term) ||
      candidate.research_overview?.toLowerCase().includes(term) ||
      (candidate as any).research_comment?.toLowerCase().includes(term) ||
      uni.name.toLowerCase().includes(term) ||
      commentContentByCandidate[candidate.id]?.toLowerCase().includes(term)
    );
  });

  const organizeComments = (comments: EvaluationComment[]) => {
    const topicGroups: { [key: string]: EvaluationComment[] } = {};
    const replies: { [key: string]: EvaluationComment[] } = {};

    comments.forEach(comment => {
      if (comment.parent_comment_id) {
        if (!replies[comment.parent_comment_id]) {
          replies[comment.parent_comment_id] = [];
        }
        replies[comment.parent_comment_id].push(comment);
      } else {
        const topic = comment.topic || 'general';
        if (!topicGroups[topic]) {
          topicGroups[topic] = [];
        }
        topicGroups[topic].push(comment);
      }
    });

    return { topicGroups, replies };
  };

  const formatCommentTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('ja-JP', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } else {
      return date.toLocaleDateString('ja-JP', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
  };

  const getCommentAuthorName = (comment: EvaluationComment) => {
    // Use stored author_name if available (most reliable)
    if (comment.author_name) return comment.author_name;
    if (comment.user_type === 'candidate') {
      return selectedCandidate?.name || '研究者';
    }
    const eid = evalToEvaluator[comment.evaluation_id as any];
    if (eid && evaluatorById[eid]) {
      const info = evaluatorById[eid];
      return info.name;
    }
    return '参加者';
  };

  const getParentInfo = (comment: EvaluationComment) => {
    if (!comment.parent_comment_id) return { author: undefined, content: undefined };
    const parent = comments.find(c => c.id === comment.parent_comment_id);
    if (parent) return { author: getCommentAuthorName(parent), content: parent.content };
    return { author: undefined, content: undefined };
  };

  const getCommentMeta = (comment: EvaluationComment): { avatarUrl?: string; university?: string; role: 'researcher' | 'evaluator' | 'reviewer' } => {
    if (comment.user_type === 'candidate') {
      return { avatarUrl: (selectedCandidate as any)?.avatar_url, university: selectedCandidate?.university, role: 'researcher' };
    }
    const eid = evalToEvaluator[comment.evaluation_id as any];
    if (eid && evaluatorById[eid]) {
      const info = evaluatorById[eid];
      return { avatarUrl: info.avatar_url, university: info.university, role: info.is_reviewer ? 'reviewer' : 'evaluator' };
    }
    return { role: 'evaluator' };
  };

  const renderComment = (comment: EvaluationComment, allReplies: { [key: string]: EvaluationComment[] }, _level = 0) => {
    const parentInfo = getParentInfo(comment);
    const meta = getCommentMeta(comment);
    return (
    <div key={comment.id}>
      <ChatBubble
        authorName={getCommentAuthorName(comment)}
        isOwn={comment.user_id === currentUserIdRef.current}
        timestamp={comment.created_at}
        parentAuthor={parentInfo.author}
        parentContent={parentInfo.content}
        avatarUrl={meta.avatarUrl}
        university={meta.university}
        role={meta.role}
        onReply={() => setReplyingTo(comment.id)}
      >
        {comment.content}
      </ChatBubble>

      {/* Reply form */}
      {replyingTo === comment.id && (
        <div className="mb-3 ml-10 md:ml-12">
          <div className="space-y-3 p-3 bg-muted/30 rounded-lg border border-border">
            <Textarea placeholder="返信内容を入力..." value={replyContent} onChange={(e) => setReplyContent(e.target.value)} rows={2} className="resize-none text-sm" autoFocus />
            <div className="flex items-center justify-end">
              <div className="flex space-x-2">
                <Button variant="ghost" size="sm" onClick={() => { setReplyingTo(null); setReplyContent(''); }}>キャンセル</Button>
                <Button size="sm" onClick={() => handleSubmitReply(comment.id, comment.topic)} disabled={!replyContent.trim() || commentLoading}>
                  {commentLoading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div> : <Send className="h-4 w-4 mr-1" />}
                  送信
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {allReplies[comment.id]?.map(reply => renderComment(reply, allReplies, _level + 1))}
    </div>
  );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">ディスカッション</h1>
        <p className="text-muted-foreground mt-1">
          すべての研究発表についてディスカッションができます
        </p>
      </div>

      {/* My Activity */}
      {(myLikedList.length > 0 || myParticipateList.length > 0 || myThreads.length > 0) && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">あなたの活動</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowMyActivity(v => !v)}>
                {showMyActivity ? '閉じる' : '開く'}
              </Button>
            </div>
          </CardHeader>
          {showMyActivity && (
            <CardContent className="space-y-4">
              {myLikedList.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center">
                    <Heart className="h-4 w-4 mr-1 text-red-500" />
                    いいねした発表
                  </h4>
                  <div className="space-y-1">
                    {myLikedList.map((r) => (
                      <div key={`like-${r.candidate_id}`} className="p-2 border rounded-md flex items-center justify-between text-sm">
                        <div>
                          <span className="font-medium">{r.research_topic}</span>
                          <span className="text-muted-foreground ml-2">{r.candidate_name}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {myParticipateList.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center">
                    <Users className="h-4 w-4 mr-1 text-green-600" />
                    参加希望した発表
                  </h4>
                  <div className="space-y-1">
                    {myParticipateList.map((r) => (
                      <div key={`part-${r.candidate_id}`} className="p-2 border rounded-md flex items-center justify-between text-sm">
                        <div>
                          <span className="font-medium">{r.research_topic}</span>
                          <span className="text-muted-foreground ml-2">{r.candidate_name}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {myThreads.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center">
                    <MessageCircle className="h-4 w-4 mr-1 text-primary" />
                    コメントした研究
                  </h4>
                  <div className="space-y-1">
                    {myThreads.map((t) => {
                      const cand = candidates.find(c => c.id === t.candidate_id);
                      return (
                        <div
                          key={`${t.candidate_id}-${t.topic}`}
                          className="p-2 border rounded-md text-sm cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => {
                            if (cand) {
                              setSelectedCandidate(cand);
                              setTimeout(() => document.getElementById('discussion-comments')?.scrollIntoView({ behavior: 'smooth' }), 100);
                            }
                          }}
                        >
                          <div className="font-medium text-primary">{t.research_topic}（{t.candidate_name}）</div>
                          <div className="text-xs text-muted-foreground">
                            {t.topic === 'general' ? '一般' : t.topic} / {t.comment_count}件 / {new Date(t.last_commented_at).toLocaleString('ja-JP')}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* Search Bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="研究者名・大学名・所属・研究課題・コメントで検索..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-10"
              />
              {searchTerm && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1 h-8 w-8 p-0"
                  onClick={() => setSearchTerm('')}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">並び替え:</span>
              <select
                className="h-9 border rounded-md px-2 bg-background"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
              >
                <option value="comments">コメント数</option>
                <option value="rating">平均評価</option>
                <option value="ratingCount">評価件数</option>
                <option value="newest">新着</option>
                <option value="university">大学別</option>
              </select>
            </div>
          </div>
          {/* University filter checkboxes */}
          <div className="flex items-center gap-4 mt-3">
            {UNIVERSITIES.map((uni) => (
              <label key={uni.id} className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={uniFilter[uni.id] ?? true}
                  onChange={(e) => setUniFilter(prev => ({ ...prev, [uni.id]: e.target.checked }))}
                  className="rounded"
                />
                <span className={`text-sm font-medium ${uni.textColor}`}>{uni.name}</span>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      {error && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        <h2 className="text-xl font-semibold">研究発表一覧</h2>
        <div className="space-y-3">
          {(() => {
            const ordered = [...filteredCandidates];
            if (currentUserType === 'candidate' && currentUserEmail) {
              ordered.sort((a, b) => {
                const aOwn = a.email === currentUserEmail ? 1 : 0;
                const bOwn = b.email === currentUserEmail ? 1 : 0;
                return bOwn - aOwn; // own first
              });
            }
            // Recent activity priority (comments + likes within 5 minutes)
            ordered.sort((a, b) => {
              const sa = (recentCounts[a.id] || 0) + (recentLikeCounts[a.id] || 0);
              const sb = (recentCounts[b.id] || 0) + (recentLikeCounts[b.id] || 0);
              return sb - sa;
            });
            // Apply selected sort
            ordered.sort((a, b) => {
              const ra = (recentCounts[a.id] || 0) + (recentLikeCounts[a.id] || 0);
              const rb = (recentCounts[b.id] || 0) + (recentLikeCounts[b.id] || 0);
              if (ra !== rb) return 0; // preserve prior recent ordering
              if (sortBy === 'comments') {
                return (commentCounts[b.id] || 0) - (commentCounts[a.id] || 0);
              }
              if (sortBy === 'rating') {
                const avg = (c: any) => {
                  const rated = (c.evaluations || []).filter((e: any) => (e.rating || 0) > 0);
                  const count = rated.length;
                  return count > 0 ? rated.reduce((s: number, e: any) => s + Math.max(0, Math.min(5, e.rating || 0)), 0) / count : 0;
                };
                return avg(b) - avg(a);
              }
              if (sortBy === 'ratingCount') {
                const cnt = (c: any) => (c.evaluations || []).filter((e: any) => (e.rating || 0) > 0).length;
                return cnt(b) - cnt(a);
              }
              if (sortBy === 'university') {
                const uniOrder: Record<string, number> = { gunma: 0, chiba: 1, tsukuba: 2 };
                return (uniOrder[a.university] ?? 9) - (uniOrder[b.university] ?? 9);
              }
              // newest fallback
              const ta = (a as any).created_at ? new Date((a as any).created_at).getTime() : 0;
              const tb = (b as any).created_at ? new Date((b as any).created_at).getTime() : 0;
              return tb - ta;
            });
            return ordered;
          })().map((candidate) => {
            const isSelected = selectedCandidate?.id === candidate.id;
            return (
              <div key={candidate.id} className="space-y-3 w-full">
                <Card
                  className={`w-full cursor-pointer transition-all ${
                    isSelected
                      ? 'ring-2 ring-primary bg-blue-50 shadow-md border-primary/20'
                      : 'hover:shadow-md hover:bg-gray-50'
                  }`}
                  onClick={() => {
                    if (isSelected) {
                      setSelectedCandidate(null);
                      setSelectedEvaluation(null);
                      return;
                    }
                    setError(null);
                    setNewComment('');
                    setNewTopic('');
                    setReplyingTo(null);
                    setReplyContent('');
                    setSelectedCandidate(candidate);
                    setSelectedEvaluation(candidate.evaluations[0] || null);
                  }}
                >
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">{candidate.research_topic}</CardTitle>
                    <CardDescription>
                      {candidate.name} - {candidate.affiliation}
                      {candidate.department && ` / ${candidate.department}`}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {(() => {
                      const rated = (candidate.evaluations || []).filter((e: any) => (e.rating || 0) > 0);
                      const count = rated.length;
                      const avg = count > 0 ? (rated.reduce((s: number, e: any) => s + Math.min(5, Math.max(0, e.rating || 0)), 0) / count) : 0;
                      const rounded = Math.max(0, Math.min(5, Math.round(avg * 2) / 2));
                      const cmTotal = commentCounts[candidate.id] || 0;
                      const rc = recentCounts[candidate.id] || 0;
                      const rl = recentLikeCounts[candidate.id] || 0;
                      const recentScore = rc + rl;
                      const heatClass = recentScore > 0 ? 'border-orange-300 bg-orange-50' : cmTotal >= 8 ? 'border-red-300 bg-red-50' : cmTotal >= 4 ? 'border-yellow-200 bg-yellow-50' : 'border-border';
                      return (
                        <div className={`flex items-center justify-between border ${heatClass} rounded-md px-3 py-2`}>
                          <div className="flex items-center gap-3">
                            <div className="flex items-center">
                              {[1,2,3,4,5].map(i => {
                                const fill = rounded >= i ? 1 : (rounded >= i - 0.5 ? 0.5 : 0);
                                if (fill === 1) {
                                  return (
                                    <svg key={i} viewBox="0 0 24 24" className="h-4 w-4 text-yellow-500" fill="currentColor" aria-hidden="true">
                                      <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.787 1.401 8.168L12 18.896l-7.335 3.87 1.401-8.168L.132 9.211l8.2-1.193z"/>
                                    </svg>
                                  );
                                }
                                if (fill === 0.5) {
                                  return (
                                    <span key={i} className="relative inline-block h-4 w-4 align-middle">
                                      <svg viewBox="0 0 24 24" className="absolute inset-0 h-4 w-4 text-muted-foreground" fill="currentColor" aria-hidden="true">
                                        <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.787 1.401 8.168L12 18.896l-7.335 3.87 1.401-8.168L.132 9.211l8.2-1.193z"/>
                                      </svg>
                                      <span className="absolute inset-0 overflow-hidden" style={{ width: '50%' }}>
                                        <svg viewBox="0 0 24 24" className="h-4 w-4 text-yellow-500" fill="currentColor" aria-hidden="true">
                                          <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.787 1.401 8.168L12 18.896l-7.335 3.87 1.401-8.168L.132 9.211l8.2-1.193z"/>
                                        </svg>
                                      </span>
                                    </span>
                                  );
                                }
                                return (
                                  <svg key={i} viewBox="0 0 24 24" className="h-4 w-4 text-muted-foreground" fill="currentColor" aria-hidden="true">
                                    <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.787 1.401 8.168L12 18.896l-7.335 3.87 1.401-8.168L.132 9.211l8.2-1.193z"/>
                                  </svg>
                                );
                              })}
                              <span className="ml-2 text-sm text-foreground">{avg.toFixed(1)} / 5</span>
                            </div>
                            <Badge variant="secondary">評価数: {count}</Badge>
                            <Badge variant="secondary" className="flex items-center gap-1">
                              <MessageCircle className="h-3 w-3" />{cmTotal}
                            </Badge>
                            {recentScore > 0 && (
                              <span className="inline-flex items-center text-xs rounded-full px-2 py-0.5 bg-orange-100 text-orange-700">
                                <Flame className="h-3 w-3 mr-1" /> 最近5分: 💬{rc}{rl ? ` ❤️${rl}` : ''}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center text-sm text-muted-foreground">
                            <MessageCircle className="h-4 w-4 mr-1 text-primary" />
                            <span>{cmTotal}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>

                {isSelected && (
                  <Card
                    className="h-full w-full"
                    onTouchStart={(e) => {
                      const t = e.changedTouches[0];
                      (e.currentTarget as any)._sx = t.clientX;
                      (e.currentTarget as any)._sy = t.clientY;
                    }}
                    onTouchEnd={(e) => {
                      const t = e.changedTouches[0];
                      const sx = (e.currentTarget as any)._sx || 0;
                      const sy = (e.currentTarget as any)._sy || 0;
                      const dx = t.clientX - sx;
                      const dy = Math.abs(t.clientY - sy);
                      if (dx > 50 && dy < 40) {
                        setSelectedCandidate(null);
                        setSelectedEvaluation(null);
                      }
                    }}
                  >
                    <CardHeader id="discussion-comments">
                      <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center">
                          <MessageCircle className="h-5 w-5 mr-2" />
                          ディスカッション
                        </CardTitle>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setReplyingTo('__new_topic__')}
                        >
                          <MessageCircle className="h-4 w-4 mr-1" />
                          新規トピック
                        </Button>
                      </div>
                      <CardDescription>
                        {candidate.research_topic}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* New Topic Form */}
                      {replyingTo === '__new_topic__' && (
                        <div className="space-y-3 p-4 rounded-lg border bg-muted/20 border-border">
                          <div className="flex items-center justify-between">
                            <h5 className="font-semibold text-sm">新しいトピックを作成</h5>
                            <Button variant="ghost" size="sm" onClick={() => { setReplyingTo(null); setNewComment(''); setNewTopic(''); }}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                          <Input placeholder="トピック名" value={newTopic} onChange={(e) => setNewTopic(e.target.value)} autoFocus />
                          <Textarea placeholder="最初のコメントを入力..." value={newComment} onChange={(e) => setNewComment(e.target.value)} rows={2} className="resize-none" />
                          <div className="flex items-center justify-end">
                            <Button size="sm" onClick={() => { handleSubmitComment(); setReplyingTo(null); }} disabled={!newTopic.trim() || !newComment.trim() || commentLoading}>
                              {commentLoading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div> : <Send className="h-4 w-4 mr-1" />}
                              作成
                            </Button>
                          </div>
                        </div>
                      )}

                      <div className="space-y-4">
                        {commentsLoading ? (
                          <div className="flex items-center justify-center py-4">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                          </div>
                        ) : comments.length === 0 ? (
                          <p className="text-muted-foreground text-center py-4">
                            まだコメントがありません。最初のコメントを投稿してください。
                          </p>
                        ) : (
                          (() => {
                            const { topicGroups, replies } = organizeComments(comments);
                            return Object.entries(topicGroups).map(([topic, topicComments]) => {
                              const topicKey = topic === 'general' ? '' : topic;
                              return (
                              <div key={topic} className="rounded-lg shadow-sm overflow-hidden border border-border bg-card">
                                <div
                                  className="px-4 py-3 border-b flex items-center justify-between cursor-pointer bg-muted/30 border-border"
                                  onClick={() => setCollapsedTopics(prev => ({ ...prev, [topic]: !prev[topic] }))}
                                >
                                  <h4 className="font-semibold text-sm text-foreground flex items-center">
                                    <MessageCircle className="h-4 w-4 text-primary mr-2" />
                                    {topic === 'general' ? '一般' : topic}
                                    <span className="ml-2 text-xs text-muted-foreground font-normal">{topicComments.length}件</span>
                                  </h4>
                                  <span className="text-xs text-muted-foreground">{collapsedTopics[topic] ? '展開' : '折りたたむ'}</span>
                                </div>
                                {!collapsedTopics[topic] && (
                                  <>
                                    <div className="p-4 space-y-1">
                                      {topicComments.map(comment =>
                                        renderComment(comment, replies, 0)
                                      )}
                                    </div>
                                    <div className="px-4 py-3 border-t border-border bg-muted/10">
                                      <div className="flex gap-2">
                                        <Input
                                          placeholder="コメントを入力..."
                                          value={newTopic === topicKey ? newComment : ''}
                                          onFocus={() => { setNewTopic(topicKey); }}
                                          onChange={(e) => { setNewTopic(topicKey); setNewComment(e.target.value); }}
                                          className="text-sm flex-1"
                                        />
                                        <Button
                                          size="sm"
                                          onClick={() => { setNewTopic(topicKey); handleSubmitComment(); }}
                                          disabled={!(newTopic === topicKey && newComment.trim()) || commentLoading}
                                        >
                                          <Send className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            );});
                          })()
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
